import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import StatusBadge from "../components/StatusBadge";
import CustomAlert from "../components/CustomAlert";

function formatDate(dateString) {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric", month: "short", day: "numeric"
  });
}



export default function Customers() {
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL || 
    (process.env.NODE_ENV === 'production' 
      ? 'https://teenagefashion.in/backend/api' 
      : 'http://localhost/newfashion/backend/api');
  const [search, setSearch] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [editMode, setEditMode] = useState(null); // 'customer' or 'measurements'
  const [editData, setEditData] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");
  const [editSuccess, setEditSuccess] = useState("");
  const [originalEditData, setOriginalEditData] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
          fetch(`${API_URL}/customers.php?all=1`, { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        setCustomers(data);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  // Filter customers by name, phone, or pin
  const filteredCustomers = useMemo(() => {
    return customers.filter(c => {
      const q = search.toLowerCase();
      return (
        (c.name && c.name.toLowerCase().includes(q)) ||
        (c.phone && c.phone.toLowerCase().includes(q)) ||
        (c.pin && c.pin.toLowerCase().includes(q))
      );
    });
  }, [search, customers]);

  // Handlers
  const openDetails = (customer) => {
    setSelectedCustomer({ ...customer, orders: [], measurements: {} });
    setShowDetails(true);
    setEditMode(null);
    setEditData(null);

    // Fetch orders
            fetch(`${API_URL}/orders.php?customer_id=${customer.id}`, { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        setSelectedCustomer(prev => ({ ...prev, orders: data.data || [] }));
      })
      .catch(() => {
        setSelectedCustomer(prev => ({ ...prev, orders: [] }));
      });

    // Fetch measurements
            fetch(`${API_URL}/measurements.php?customer_id=${customer.id}`, { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        setSelectedCustomer(prev => ({ ...prev, measurements: data || {} }));
      })
      .catch(() => {
        setSelectedCustomer(prev => ({ ...prev, measurements: {} }));
      });
  };
  const closeDetails = () => {
    setShowDetails(false);
    setEditMode(null);
    setEditData(null);
  };
  const openEdit = (mode) => {
    setEditMode(mode);
    
    // Ensure all measurement fields are properly initialized
    const measurementFields = [
      // Shirt measurements
      'l_shirt', 's_shirt', 'h1_shirt', 'h2_shirt', 'h3_shirt', 'h4_shirt', 'c_shirt', 'w_shirt', 'bx_shirt', 'loos1_shirt', 'loos2_shirt', 'loos3_shirt', 'n_shirt', 'cpoint_shirt', 'pocket_shirt',
      // Pant measurements
      'l_pant', 'w_pant', 'bx_pant', 'f_pant', 'l2_pant', 'b_pant', 'knee_pant', 'thigh_pant', 'pocket_pant', 'fleet_pant',
      // Churidar measurements
      'l_cd', 's_cd', 'h1_cd', 'h2_cd', 'h3_cd', 'h4_cd', 'c_cd', 'w_cd', 'h_cd', 'bx_cd', 'n_f_cd', 'n_b_cd'
    ];
    
    const initializedMeasurements = {};
    measurementFields.forEach(field => {
      initializedMeasurements[field] = selectedCustomer.measurements[field] || '';
    });
    
    const editDataWithMeasurements = {
      ...selectedCustomer,
      measurements: initializedMeasurements
    };
    
    setEditData(editDataWithMeasurements);
    // Store original data for comparison
    setOriginalEditData(JSON.parse(JSON.stringify(editDataWithMeasurements)));
    setEditSuccess("");
    setEditError("");
    setEditLoading(false);
  };
  const closeEdit = () => {
    setEditMode(null);
    setEditData(null);
    setOriginalEditData(null);
    setEditSuccess("");
    setEditError("");
    setEditLoading(false);
  };

  const hasCustomerDataChanged = () => {
    if (!originalEditData || !editData) return false;
    
    return (
      originalEditData.name !== editData.name ||
      originalEditData.phone !== editData.phone ||
      originalEditData.address !== editData.address ||
      originalEditData.pin !== editData.pin ||
      originalEditData.notes !== editData.notes
    );
  };

  const hasMeasurementsDataChanged = () => {
    if (!originalEditData || !editData) return false;
    
    const original = originalEditData.measurements;
    const current = editData.measurements;
    
    // Check all measurement fields
    const measurementFields = [
      // Shirt measurements
      'l_shirt', 's_shirt', 'h1_shirt', 'h2_shirt', 'h3_shirt', 'h4_shirt', 'c_shirt', 'w_shirt', 'bx_shirt', 'loos1_shirt', 'loos2_shirt', 'loos3_shirt', 'n_shirt', 'cpoint_shirt', 'pocket_shirt',
      // Pant measurements
      'l_pant', 'w_pant', 'bx_pant', 'f_pant', 'l2_pant', 'b_pant', 'knee_pant', 'thigh_pant', 'pocket_pant', 'fleet_pant',
      // Churidar measurements
      'l_cd', 's_cd', 'h1_cd', 'h2_cd', 'h3_cd', 'h4_cd', 'c_cd', 'w_cd', 'h_cd', 'bx_cd', 'n_f_cd', 'n_b_cd'
    ];
    
    for (const field of measurementFields) {
      if (original[field] !== current[field]) {
        return true;
      }
    }
    
    return false;
  };

  const getImageUrl = (image_path) => {
    if (!image_path) return null;
    if (image_path.startsWith("http")) return image_path;
    return `${process.env.NODE_ENV === "production" ? "https://teenagefashion.in" : "http://localhost/newfashion"}/${image_path}`;
  };

  // Handle customer deletion
  const handleDeleteCustomer = async (customerId, customerName) => {
    setCustomerToDelete({ id: customerId, name: customerName });
    setShowDeleteAlert(true);
  };

  const confirmDeleteCustomer = async () => {
    if (!customerToDelete) return;

    setDeleteLoading(true);
    setDeleteError("");

    try {
      const res = await fetch(`${API_URL}/delete_customer.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: customerToDelete.id }),
        credentials: 'include'
      });
      
      const result = await res.json();
      
      if (result.success) {
        // Remove customer from the list
        setCustomers(prev => prev.filter(c => c.id !== customerToDelete.id));
        
        // Close details modal if it's open for this customer
        if (selectedCustomer && selectedCustomer.id === customerToDelete.id) {
          closeDetails();
        }
        
        // Show success message
        setDeleteError(""); // Clear any previous errors
        // You can add a success state here if needed
      } else {
        setDeleteError(result.error || "Failed to delete customer.");
      }
    } catch (err) {
      setDeleteError("Failed to delete customer.");
    } finally {
      setDeleteLoading(false);
      setShowDeleteAlert(false);
      setCustomerToDelete(null);
    }
  };

  const cancelDeleteCustomer = () => {
    setShowDeleteAlert(false);
    setCustomerToDelete(null);
  };

  // Handle Add Customer navigation
  const handleAddCustomer = () => {
    navigate("/add-customer");
  };

  return (
    <div className="max-w-7xl mx-auto w-full py-4">
      {/* Page Title */}
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Customers</h1>
      {/* Page Description */}
      <p className="text-gray-600 text-sm mb-4">View, search, and manage your customer records and their measurements.</p>
      <div className="bg-white rounded-xl shadow-sm p-0 sm:p-4 lg:p-8">
        {/* Error Display */}
        {deleteError && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
            {deleteError}
          </div>
        )}
        {/* Header: Search + Add Customer */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6 w-full">
          <div className="flex-1 flex flex-row gap-2 w-full">
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, phone, or PIN..."
              className="flex-1 sm:w-full sm:max-w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm min-w-0"
            />
            <button
              onClick={handleAddCustomer}
              className="px-2 py-1 text-xs sm:px-4 sm:py-2 sm:text-sm bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition whitespace-nowrap"
              style={{ minWidth: 80 }}
            >
              Add Customer
            </button>
          </div>
        </div>

        {/* Customer Table */}
        <div className="overflow-x-auto w-full bg-gray-50 rounded-lg" style={{padding: 0, margin: 0}}>
          <table className="min-w-full w-full text-sm border border-gray-200 rounded-lg bg-white">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-200">
                <th className="py-3 px-2 font-medium min-w-[120px]">Name</th>
                <th className="py-3 px-2 font-medium min-w-[120px] hidden xs:table-cell">Phone</th>
                <th className="py-3 px-2 font-medium min-w-[200px] hidden sm:table-cell">Address</th>
                <th className="py-3 px-2 font-medium min-w-[150px]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4} className="text-center py-8 text-gray-400">Loading...</td></tr>
              ) : error ? (
                <tr><td colSpan={4} className="text-center py-8 text-red-400">{error}</td></tr>
              ) : filteredCustomers.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center py-8 text-gray-400">No customers found</td>
                </tr>
              ) : (
                filteredCustomers.map(c => (
                  <tr
                    key={c.id}
                    className="border-b border-gray-100 hover:bg-gray-50 transition cursor-pointer"
                    onClick={e => {
                      if (e.target.tagName !== "BUTTON") openDetails(c);
                    }}
                  >
                    <td className="py-3 px-2 text-blue-600 font-medium min-w-[120px] cursor-pointer" onClick={() => openDetails(c)}>
                      {c.name}
                      {c.phone && (
                        <div className="mt-1 inline-block bg-gray-200 text-gray-900 text-xs px-2 py-0.5 rounded-full font-normal align-middle">
                          {c.phone}
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-2 text-gray-700 min-w-[120px] hidden xs:table-cell">{c.phone}</td>
                    <td className="py-3 px-2 text-gray-700 min-w-[200px] hidden sm:table-cell">{c.address || '-'}</td>
                    <td className="py-3 px-2 min-w-[100px]">
                      <div className="flex gap-1">
                        <button
                          onClick={() => openDetails(c)}
                          className="text-green-600 hover:text-green-800 text-sm font-medium px-2 py-1 rounded"
                        >
                          View
                        </button>
                        <button
                          onClick={() => openDetails(c)}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium px-2 py-1 rounded"
                        >
                          Edit
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteCustomer(c.id, c.name);
                          }}
                          disabled={deleteLoading}
                          className="text-red-600 hover:text-red-800 text-sm font-medium px-2 py-1 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {deleteLoading ? "..." : "Delete"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Customer Details Modal */}
      {showDetails && selectedCustomer && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-30 backdrop-blur-sm modal-open-block-swipe">
          <div className="bg-white rounded-xl shadow-lg max-w-2xl w-full mx-2 sm:mx-0 max-h-[90vh] overflow-y-auto p-6 relative animate-fadeIn">
            {/* Close button */}
            <button
              onClick={closeDetails}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 text-2xl font-bold focus:outline-none"
              aria-label="Close"
            >
              ×
            </button>
            {/* Customer Info */}
            <h2 className="text-xl font-bold mb-2 text-gray-900">{selectedCustomer.name}</h2>
            <div className="mb-2 text-gray-700 text-sm">
              <div><span className="font-medium">ID:</span> {selectedCustomer.id}</div>
              <div><span className="font-medium">Phone:</span> {selectedCustomer.phone}</div>
              <div><span className="font-medium">PIN:</span> {selectedCustomer.pin}</div>
              <div><span className="font-medium">Address:</span> {selectedCustomer.address}</div>
              {selectedCustomer.notes && <div><span className="font-medium">Notes:</span> {selectedCustomer.notes}</div>}
            </div>
            {/* Order History */}
            <div className="mb-4">
              <h3 className="font-semibold text-gray-900 mb-2">Order History</h3>
              <div className="overflow-x-auto">
                <table className="min-w-[400px] w-full text-xs border border-gray-200 rounded">
                  <thead>
                    <tr className="text-left text-gray-500 border-b border-gray-200">
                      <th className="py-2 px-2 font-medium">Order ID</th>
                      <th className="py-2 px-2 font-medium">Image</th>
                      <th className="py-2 px-2 font-medium">Delivery Date</th>
                      <th className="py-2 px-2 font-medium">Status</th>
                      <th className="py-2 px-2 font-medium">Total Amount</th>
                      <th className="py-2 px-2 font-medium">Paid</th>
                      <th className="py-2 px-2 font-medium">Balance</th>
                      <th className="py-2 px-2 font-medium">Quantity</th>
                      <th className="py-2 px-2 font-medium">Fabric Type</th>
                      <th className="py-2 px-2 font-medium">Color</th>
                      <th className="py-2 px-2 font-medium">Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!selectedCustomer.orders || selectedCustomer.orders.length === 0 ? (
                      <tr><td colSpan={11} className="text-center py-4 text-gray-400">No orders</td></tr>
                    ) : (
                      selectedCustomer.orders.map(order => (
                        <tr key={order.id} className="border-b border-gray-100">
                          <td className="py-2 px-2 text-gray-900 font-medium">#{order.id}</td>
                          <td className="py-2 px-2">
                            {order.image_path ? (
                              <img
                                src={getImageUrl(order.image_path)}
                                alt="Order"
                                className="w-8 h-8 object-cover rounded border cursor-pointer"
                                style={{ minWidth: 32, minHeight: 32 }}
                                onClick={() => setPreviewUrl(getImageUrl(order.image_path))}
                              />
                            ) : (
                              <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded border text-gray-400 text-lg" style={{ minWidth: 32, minHeight: 32 }}>?</div>
                            )}
                          </td>
                          <td className="py-2 px-2 text-gray-700">{formatDate(order.delivery_date)}</td>
                          <td className="py-0.5 px-1">
                            <span className="text-xs">
                              <StatusBadge status={order.status} />
                            </span>
                          </td>
                          <td className="py-2 px-2 text-gray-900">₹{order.total_amount}</td>
                          <td className="py-2 px-2 text-gray-900">₹{order.paid}</td>
                          <td className="py-2 px-2 text-gray-900">₹{order.balance}</td>
                          <td className="py-2 px-2 text-gray-900">{order.quantity}</td>
                          <td className="py-2 px-2 text-gray-900">{order.fabric_type}</td>
                          <td className="py-2 px-2 text-gray-900">{order.color}</td>
                          <td className="py-2 px-2 text-gray-900">{order.note}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            {/* Image Preview Modal */}
            {previewUrl && (
              <div
                className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80"
                onClick={() => setPreviewUrl(null)}
              >
                <div className="relative max-w-xs md:max-w-lg w-full p-2" onClick={e => e.stopPropagation()}>
                  <button
                    className="absolute top-2 right-2 text-white text-2xl bg-black bg-opacity-40 rounded-full w-8 h-8 flex items-center justify-center focus:outline-none"
                    onClick={() => setPreviewUrl(null)}
                    aria-label="Close preview"
                  >
                    &times;
                  </button>
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="w-full h-auto max-h-[80vh] rounded shadow-lg border-2 border-white"
                  />
                </div>
              </div>
            )}
            {/* Measurements */}
            <div className="mb-4">
              <h3 className="font-semibold text-gray-900 mb-2">Measurements</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <h4 className="font-medium text-gray-800 mb-1">Shirt</h4>
                  <ul className="text-xs text-gray-700 space-y-1">
                    {selectedCustomer.measurements ? (() => {
                      const shirtMeasurements = [
                        { key: 'L', value: selectedCustomer.measurements.l_shirt },
                        { key: 'S', value: selectedCustomer.measurements.s_shirt },
                        { key: 'H1', value: selectedCustomer.measurements.h1_shirt },
                        { key: 'H2', value: selectedCustomer.measurements.h2_shirt },
                        { key: 'H3', value: selectedCustomer.measurements.h3_shirt },
                        { key: 'H4', value: selectedCustomer.measurements.h4_shirt },
                        { key: 'C', value: selectedCustomer.measurements.c_shirt },
                        { key: 'W', value: selectedCustomer.measurements.w_shirt },
                        { key: 'Bx', value: selectedCustomer.measurements.bx_shirt },
                        { key: 'Loos1', value: selectedCustomer.measurements.loos1_shirt },
                        { key: 'Loos2', value: selectedCustomer.measurements.loos2_shirt },
                        { key: 'Loos3', value: selectedCustomer.measurements.loos3_shirt },
                        { key: 'N', value: selectedCustomer.measurements.n_shirt },
                        { key: 'CPoint', value: selectedCustomer.measurements.cpoint_shirt },
                        { key: 'Pocket', value: selectedCustomer.measurements.pocket_shirt }
                      ].filter(item => item.value && item.value !== '0' && item.value !== 0);
                      
                      return shirtMeasurements.length > 0 ? 
                        shirtMeasurements.map((item) => (
                          <li key={item.key}><span className="font-medium">{item.key}:</span> {item.value}</li>
                        )) : 
                        <li>No shirt measurements</li>;
                    })() : <li>No shirt measurements</li>}
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium text-gray-800 mb-1">Pant</h4>
                  <ul className="text-xs text-gray-700 space-y-1">
                    {selectedCustomer.measurements ? (() => {
                      const pantMeasurements = [
                        { key: 'L', value: selectedCustomer.measurements.l_pant },
                        { key: 'W2', value: selectedCustomer.measurements.w_pant },
                        { key: 'Bx', value: selectedCustomer.measurements.bx_pant },
                        { key: 'F', value: selectedCustomer.measurements.f_pant },
                        { key: 'L2', value: selectedCustomer.measurements.l2_pant },
                        { key: 'B', value: selectedCustomer.measurements.b_pant },
                        { key: 'Knee', value: selectedCustomer.measurements.knee_pant },
                        { key: 'Thigh', value: selectedCustomer.measurements.thigh_pant },
                        { key: 'Pocket', value: selectedCustomer.measurements.pocket_pant },
                        { key: 'Fleet', value: selectedCustomer.measurements.fleet_pant }
                      ].filter(item => item.value && item.value !== '0' && item.value !== 0);
                      
                      return pantMeasurements.length > 0 ? 
                        pantMeasurements.map((item) => (
                          <li key={item.key}><span className="font-medium">{item.key}:</span> {item.value}</li>
                        )) : 
                        <li>No pant measurements</li>;
                    })() : <li>No pant measurements</li>}
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium text-gray-800 mb-1">Ladies</h4>
                  <ul className="text-xs text-gray-700 space-y-1">
                    {selectedCustomer.measurements ? (() => {
                      const ladiesMeasurements = [
                        { key: 'L', value: selectedCustomer.measurements.l_cd },
                        { key: 'S', value: selectedCustomer.measurements.s_cd },
                        { key: 'H1', value: selectedCustomer.measurements.h1_cd },
                        { key: 'H2', value: selectedCustomer.measurements.h2_cd },
                        { key: 'H3', value: selectedCustomer.measurements.h3_cd },
                        { key: 'H4', value: selectedCustomer.measurements.h4_cd },
                        { key: 'C', value: selectedCustomer.measurements.c_cd },
                        { key: 'W', value: selectedCustomer.measurements.w_cd },
                        { key: 'H', value: selectedCustomer.measurements.h_cd },
                        { key: 'Bx', value: selectedCustomer.measurements.bx_cd },
                        { key: 'NF', value: selectedCustomer.measurements.n_f_cd },
                        { key: 'NB', value: selectedCustomer.measurements.n_b_cd }
                      ].filter(item => item.value && item.value !== '0' && item.value !== 0);
                      
                      return ladiesMeasurements.length > 0 ? 
                        ladiesMeasurements.map((item) => (
                          <li key={item.key}><span className="font-medium">{item.key}:</span> {item.value}</li>
                        )) : 
                        <li>No ladies measurements</li>;
                    })() : <li>No ladies measurements</li>}
                  </ul>
                </div>
              </div>
            </div>
            {/* Modal Buttons */}
            <div className="flex flex-col sm:flex-row gap-2 mt-4">
              <button
                onClick={() => openEdit('measurements')}
                className="flex-1 bg-blue-100 text-blue-700 py-2 px-4 rounded-lg hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm font-medium"
              >
                Edit Measurements
              </button>
              <button
                onClick={() => openEdit('customer')}
                className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400 text-sm font-medium"
              >
                Edit Customer
              </button>
              <button
                onClick={() => handleDeleteCustomer(selectedCustomer.id, selectedCustomer.name)}
                disabled={deleteLoading}
                className="flex-1 bg-red-100 text-red-700 py-2 px-4 rounded-lg hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-400 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleteLoading ? "Deleting..." : "Delete Customer"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modals (simple mockup, scrollable) */}
      {editMode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm modal-open-block-swipe">
          <div className="bg-white rounded-xl shadow-lg max-w-lg w-full mx-2 max-h-[90vh] overflow-y-auto p-6 relative animate-fadeIn">
            <button
              onClick={closeEdit}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 text-2xl font-bold focus:outline-none"
              aria-label="Close"
            >
              ×
            </button>
            <h2 className="text-lg font-bold mb-4 text-gray-900">
              {editMode === 'customer' ? 'Edit Customer Info' : 'Edit Measurements'}
            </h2>
            {/* Simple form mockup, not functional */}
            {editMode === 'customer' ? (
              <form
                className="space-y-4"
                onSubmit={async (e) => {
                  e.preventDefault();
                  
                  // Check if any changes were made
                  if (!hasCustomerDataChanged()) {
                    setEditSuccess("No changes detected. Closing modal...");
                    setTimeout(() => {
                      setEditMode(null);
                      setEditData(null);
                    }, 1000);
                    return;
                  }
                  
                  setEditLoading(true);
                  setEditError("");
                  setEditSuccess("");
                  const payload = {
                    id: editData.id,
                    name: editData.name,
                    phone: editData.phone,
                    address: editData.address,
                    pin: editData.pin,
                    notes: editData.notes || ''
                  };
                  try {
                    const res = await fetch(`${API_URL}/update_customer.php`, {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify(payload),
                      credentials: 'include'
                    });
                    const result = await res.json();
                    if (result.success) {
                      setEditSuccess("Customer updated successfully!");
                      setSelectedCustomer((prev) => ({
                        ...prev,
                        name: editData.name,
                        phone: editData.phone,
                        address: editData.address,
                        pin: editData.pin,
                        notes: editData.notes || ''
                      }));
                      // Optionally update customers list in table
                      setCustomers((prev) =>
                        prev.map((c) =>
                          c.id === editData.id
                            ? { ...c, name: editData.name, phone: editData.phone, address: editData.address, pin: editData.pin, notes: editData.notes || '' }
                            : c
                        )
                      );
                      setTimeout(() => {
                        setEditMode(null);
                        setEditData(null);
                      }, 1000);
                    } else {
                      // Handle duplicate errors specifically
                      if (res.status === 409 && result.duplicate_type) {
                        const fieldName = result.duplicate_type === 'phone' ? 'phone' : 'pin';
                        const existingCustomer = result.existing_customer;
                        setEditError(`${result.error}. This ${result.duplicate_type} is already registered to customer "${existingCustomer}".`);
                      } else {
                        setEditError(result.error || "Failed to update customer.");
                      }
                    }
                  } catch (err) {
                    setEditError("Failed to update customer.");
                  } finally {
                    setEditLoading(false);
                  }
                }}
              >
                <div>
                  <label className="block text-sm font-medium mb-1">Name</label>
                  <input
                    type="text"
                    value={editData.name}
                    onChange={e => setEditData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Phone</label>
                  <input
                    type="text"
                    value={editData.phone}
                    onChange={e => setEditData(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Address</label>
                  <input
                    type="text"
                    value={editData.address}
                    onChange={e => setEditData(prev => ({ ...prev, address: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">PIN</label>
                  <input
                    type="text"
                    value={editData.pin}
                    onChange={e => setEditData(prev => ({ ...prev, pin: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Notes</label>
                  <textarea
                    value={editData.notes || ''}
                    onChange={e => setEditData(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    rows={2}
                  />
                </div>
                {editError && <div className="text-red-500 text-xs mt-2">{editError}</div>}
                {editSuccess && <div className="text-green-600 text-xs mt-2">{editSuccess}</div>}
                <div className="flex flex-row gap-2 mt-4">
                  <button
                    type="submit"
                    disabled={editLoading}
                    className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-medium"
                  >
                    {editLoading ? "Saving..." : "Save"}
                  </button>
                  <button
                    type="button"
                    onClick={closeEdit}
                    className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 text-sm font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <form className="space-y-6" onSubmit={async (e) => {
                e.preventDefault();
                
                // Check if any changes were made
                if (!hasMeasurementsDataChanged()) {
                  setEditSuccess("No changes detected. Closing modal...");
                  setTimeout(() => {
                    setEditMode(null);
                    setEditData(null);
                  }, 1000);
                  return;
                }
                
                setEditLoading(true);
                setEditError("");
                setEditSuccess("");
                // Prepare payload
                const payload = {
                  customer_id: editData.id,
                  // Shirt measurements
                  l_shirt: editData.measurements.l_shirt || 0,
                  s_shirt: editData.measurements.s_shirt || 0,
                  h1_shirt: editData.measurements.h1_shirt || 0,
                  h2_shirt: editData.measurements.h2_shirt || 0,
                  h3_shirt: editData.measurements.h3_shirt || 0,
                  h4_shirt: editData.measurements.h4_shirt || 0,
                  c_shirt: editData.measurements.c_shirt || 0,
                  w_shirt: editData.measurements.w_shirt || 0,
                  bx_shirt: editData.measurements.bx_shirt || 0,
                  loos1_shirt: editData.measurements.loos1_shirt || 0,
                  loos2_shirt: editData.measurements.loos2_shirt || 0,
                  loos3_shirt: editData.measurements.loos3_shirt || 0,
                  n_shirt: editData.measurements.n_shirt || 0,
                  cpoint_shirt: editData.measurements.cpoint_shirt || 0,
                  pocket_shirt: editData.measurements.pocket_shirt || 0,
                  // Pant measurements
                  l_pant: editData.measurements.l_pant || 0,
                  w_pant: editData.measurements.w_pant || 0,
                  bx_pant: editData.measurements.bx_pant || 0,
                  f_pant: editData.measurements.f_pant || 0,
                  l2_pant: editData.measurements.l2_pant || 0,
                  b_pant: editData.measurements.b_pant || 0,
                  knee_pant: editData.measurements.knee_pant || 0,
                  thigh_pant: editData.measurements.thigh_pant || 0,
                  pocket_pant: editData.measurements.pocket_pant || 0,
                  fleet_pant: editData.measurements.fleet_pant || 0,
                  // Churidar measurements
                  l_cd: editData.measurements.l_cd || 0,
                  s_cd: editData.measurements.s_cd || 0,
                  h1_cd: editData.measurements.h1_cd || 0,
                  h2_cd: editData.measurements.h2_cd || 0,
                  h3_cd: editData.measurements.h3_cd || 0,
                  h4_cd: editData.measurements.h4_cd || 0,
                  c_cd: editData.measurements.c_cd || 0,
                  w_cd: editData.measurements.w_cd || 0,
                  h_cd: editData.measurements.h_cd || 0,
                  bx_cd: editData.measurements.bx_cd || 0,
                  n_f_cd: editData.measurements.n_f_cd || 0,
                  n_b_cd: editData.measurements.n_b_cd || 0,
                };
                try {
                  const res = await fetch(`${API_URL}/update_measurements.php`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                    credentials: 'include'
                  });
                  const result = await res.json();
                  if (result.success) {
                    setEditSuccess("Measurements updated successfully!");
                    // Update selectedCustomer.measurements with the new data
                    setSelectedCustomer(prev => ({ ...prev, measurements: editData.measurements }));
                    setTimeout(() => { 
                      setEditMode(null); 
                      setEditData(null); 
                    }, 1000);
                  } else {
                    setEditError(result.error || "Failed to update measurements.");
                  }
                } catch (err) {
                  setEditError("Failed to update measurements.");
                } finally {
                  setEditLoading(false);
                }
              }}>
                {/* Shirt Measurements */}
                <div>
                  <h4 className="font-medium text-gray-800 mb-3 text-sm">Shirt Measurements</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                    <div>
                      <label className="block text-xs font-medium mb-1">L</label>
                      <input
                        type="number"
                        value={editData.measurements.l_shirt || ''}
                        onChange={e => setEditData(prev => ({
                          ...prev,
                          measurements: { ...prev.measurements, l_shirt: e.target.value }
                        }))}
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                        step="0.1"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">S</label>
                      <input
                        type="number"
                        value={editData.measurements.s_shirt || ''}
                        onChange={e => setEditData(prev => ({
                          ...prev,
                          measurements: { ...prev.measurements, s_shirt: e.target.value }
                        }))}
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                        step="0.1"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">H1</label>
                      <input
                        type="number"
                        value={editData.measurements.h1_shirt || ''}
                        onChange={e => setEditData(prev => ({
                          ...prev,
                          measurements: { ...prev.measurements, h1_shirt: e.target.value }
                        }))}
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                        step="0.1"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">H2</label>
                      <input
                        type="number"
                        value={editData.measurements.h2_shirt || ''}
                        onChange={e => setEditData(prev => ({
                          ...prev,
                          measurements: { ...prev.measurements, h2_shirt: e.target.value }
                        }))}
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                        step="0.1"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">H3</label>
                      <input
                        type="number"
                        value={editData.measurements.h3_shirt || ''}
                        onChange={e => setEditData(prev => ({
                          ...prev,
                          measurements: { ...prev.measurements, h3_shirt: e.target.value }
                        }))}
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                        step="0.1"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">H4</label>
                      <input
                        type="number"
                        value={editData.measurements.h4_shirt || ''}
                        onChange={e => setEditData(prev => ({
                          ...prev,
                          measurements: { ...prev.measurements, h4_shirt: e.target.value }
                        }))}
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                        step="0.1"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">C</label>
                      <input
                        type="number"
                        value={editData.measurements.c_shirt || ''}
                        onChange={e => setEditData(prev => ({
                          ...prev,
                          measurements: { ...prev.measurements, c_shirt: e.target.value }
                        }))}
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                        step="0.1"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">W</label>
                      <input
                        type="number"
                        value={editData.measurements.w_shirt || ''}
                        onChange={e => setEditData(prev => ({
                          ...prev,
                          measurements: { ...prev.measurements, w_shirt: e.target.value }
                        }))}
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                        step="0.1"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">Bx</label>
                      <input
                        type="number"
                        value={editData.measurements.bx_shirt || ''}
                        onChange={e => setEditData(prev => ({
                          ...prev,
                          measurements: { ...prev.measurements, bx_shirt: e.target.value }
                        }))}
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                        step="0.1"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">Loos1</label>
                      <input
                        type="number"
                        value={editData.measurements.loos1_shirt || ''}
                        onChange={e => setEditData(prev => ({
                          ...prev,
                          measurements: { ...prev.measurements, loos1_shirt: e.target.value }
                        }))}
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                        step="0.1"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">Loos2</label>
                      <input
                        type="number"
                        value={editData.measurements.loos2_shirt || ''}
                        onChange={e => setEditData(prev => ({
                          ...prev,
                          measurements: { ...prev.measurements, loos2_shirt: e.target.value }
                        }))}
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                        step="0.1"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">Loos3</label>
                      <input
                        type="number"
                        value={editData.measurements.loos3_shirt || ''}
                        onChange={e => setEditData(prev => ({
                          ...prev,
                          measurements: { ...prev.measurements, loos3_shirt: e.target.value }
                        }))}
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                        step="0.1"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">N</label>
                      <input
                        type="number"
                        value={editData.measurements.n_shirt || ''}
                        onChange={e => setEditData(prev => ({
                          ...prev,
                          measurements: { ...prev.measurements, n_shirt: e.target.value }
                        }))}
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                        step="0.1"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">CPoint</label>
                      <input
                        type="number"
                        value={editData.measurements.cpoint_shirt || ''}
                        onChange={e => setEditData(prev => ({
                          ...prev,
                          measurements: { ...prev.measurements, cpoint_shirt: e.target.value }
                        }))}
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                        step="0.1"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">Pocket</label>
                      <input
                        type="number"
                        value={editData.measurements.pocket_shirt || ''}
                        onChange={e => setEditData(prev => ({
                          ...prev,
                          measurements: { ...prev.measurements, pocket_shirt: e.target.value }
                        }))}
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                        step="0.1"
                      />
                    </div>
                  </div>
                </div>

                {/* Pant Measurements */}
                <div>
                  <h4 className="font-medium text-gray-800 mb-3 text-sm">Pant Measurements</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                    <div>
                      <label className="block text-xs font-medium mb-1">L</label>
                      <input
                        type="number"
                        value={editData.measurements.l_pant || ''}
                        onChange={e => setEditData(prev => ({
                          ...prev,
                          measurements: { ...prev.measurements, l_pant: e.target.value }
                        }))}
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                        step="0.1"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">W2</label>
                      <input
                        type="number"
                        value={editData.measurements.w_pant || ''}
                        onChange={e => setEditData(prev => ({
                          ...prev,
                          measurements: { ...prev.measurements, w_pant: e.target.value }
                        }))}
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                        step="0.1"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">Bx</label>
                      <input
                        type="number"
                        value={editData.measurements.bx_pant || ''}
                        onChange={e => setEditData(prev => ({
                          ...prev,
                          measurements: { ...prev.measurements, bx_pant: e.target.value }
                        }))}
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                        step="0.1"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">F</label>
                      <input
                        type="number"
                        value={editData.measurements.f_pant || ''}
                        onChange={e => setEditData(prev => ({
                          ...prev,
                          measurements: { ...prev.measurements, f_pant: e.target.value }
                        }))}
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                        step="0.1"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">L2</label>
                      <input
                        type="number"
                        value={editData.measurements.l2_pant || ''}
                        onChange={e => setEditData(prev => ({
                          ...prev,
                          measurements: { ...prev.measurements, l2_pant: e.target.value }
                        }))}
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                        step="0.1"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">B</label>
                      <input
                        type="number"
                        value={editData.measurements.b_pant || ''}
                        onChange={e => setEditData(prev => ({
                          ...prev,
                          measurements: { ...prev.measurements, b_pant: e.target.value }
                        }))}
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                        step="0.1"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">Knee</label>
                      <input
                        type="number"
                        value={editData.measurements.knee_pant || ''}
                        onChange={e => setEditData(prev => ({
                          ...prev,
                          measurements: { ...prev.measurements, knee_pant: e.target.value }
                        }))}
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                        step="0.1"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">Thigh</label>
                      <input
                        type="number"
                        value={editData.measurements.thigh_pant || ''}
                        onChange={e => setEditData(prev => ({
                          ...prev,
                          measurements: { ...prev.measurements, thigh_pant: e.target.value }
                        }))}
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                        step="0.1"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">Pocket</label>
                      <input
                        type="number"
                        value={editData.measurements.pocket_pant || ''}
                        onChange={e => setEditData(prev => ({
                          ...prev,
                          measurements: { ...prev.measurements, pocket_pant: e.target.value }
                        }))}
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                        step="0.1"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">Fleet</label>
                      <input
                        type="number"
                        value={editData.measurements.fleet_pant || ''}
                        onChange={e => setEditData(prev => ({
                          ...prev,
                          measurements: { ...prev.measurements, fleet_pant: e.target.value }
                        }))}
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                        step="0.1"
                      />
                    </div>
                  </div>
                </div>

                {/* Ladies Measurements */}
                <div>
                  <h4 className="font-medium text-gray-800 mb-3 text-sm">Ladies Measurements</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                    <div>
                      <label className="block text-xs font-medium mb-1">L</label>
                      <input
                        type="number"
                        value={editData.measurements.l_cd || ''}
                        onChange={e => setEditData(prev => ({
                          ...prev,
                          measurements: { ...prev.measurements, l_cd: e.target.value }
                        }))}
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                        step="0.1"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">S</label>
                      <input
                        type="number"
                        value={editData.measurements.s_cd || ''}
                        onChange={e => setEditData(prev => ({
                          ...prev,
                          measurements: { ...prev.measurements, s_cd: e.target.value }
                        }))}
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                        step="0.1"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">H1</label>
                      <input
                        type="number"
                        value={editData.measurements.h1_cd || ''}
                        onChange={e => setEditData(prev => ({
                          ...prev,
                          measurements: { ...prev.measurements, h1_cd: e.target.value }
                        }))}
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                        step="0.1"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">H2</label>
                      <input
                        type="number"
                        value={editData.measurements.h2_cd || ''}
                        onChange={e => setEditData(prev => ({
                          ...prev,
                          measurements: { ...prev.measurements, h2_cd: e.target.value }
                        }))}
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                        step="0.1"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">H3</label>
                      <input
                        type="number"
                        value={editData.measurements.h3_cd || ''}
                        onChange={e => setEditData(prev => ({
                          ...prev,
                          measurements: { ...prev.measurements, h3_cd: e.target.value }
                        }))}
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                        step="0.1"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">H4</label>
                      <input
                        type="number"
                        value={editData.measurements.h4_cd || ''}
                        onChange={e => setEditData(prev => ({
                          ...prev,
                          measurements: { ...prev.measurements, h4_cd: e.target.value }
                        }))}
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                        step="0.1"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">C</label>
                      <input
                        type="number"
                        value={editData.measurements.c_cd || ''}
                        onChange={e => setEditData(prev => ({
                          ...prev,
                          measurements: { ...prev.measurements, c_cd: e.target.value }
                        }))}
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                        step="0.1"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">W</label>
                      <input
                        type="number"
                        value={editData.measurements.w_cd || ''}
                        onChange={e => setEditData(prev => ({
                          ...prev,
                          measurements: { ...prev.measurements, w_cd: e.target.value }
                        }))}
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                        step="0.1"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">H</label>
                      <input
                        type="number"
                        value={editData.measurements.h_cd || ''}
                        onChange={e => setEditData(prev => ({
                          ...prev,
                          measurements: { ...prev.measurements, h_cd: e.target.value }
                        }))}
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                        step="0.1"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">Bx</label>
                      <input
                        type="number"
                        value={editData.measurements.bx_cd || ''}
                        onChange={e => setEditData(prev => ({
                          ...prev,
                          measurements: { ...prev.measurements, bx_cd: e.target.value }
                        }))}
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                        step="0.1"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">NF</label>
                      <input
                        type="number"
                        value={editData.measurements.n_f_cd || ''}
                        onChange={e => setEditData(prev => ({
                          ...prev,
                          measurements: { ...prev.measurements, n_f_cd: e.target.value }
                        }))}
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                        step="0.1"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">NB</label>
                      <input
                        type="number"
                        value={editData.measurements.n_b_cd || ''}
                        onChange={e => setEditData(prev => ({
                          ...prev,
                          measurements: { ...prev.measurements, n_b_cd: e.target.value }
                        }))}
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                        step="0.1"
                      />
                    </div>
                  </div>
                </div>

                {editError && <div className="text-red-500 text-xs mt-2">{editError}</div>}
                {editSuccess && <div className="text-green-600 text-xs mt-2">{editSuccess}</div>}
                <div className="flex flex-row gap-2 mt-4">
                  <button type="submit" disabled={editLoading} className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-medium">
                    {editLoading ? "Saving..." : "Save"}
                  </button>
                  <button type="button" onClick={closeEdit} className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 text-sm font-medium">Cancel</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Custom Alert for Delete Confirmation */}
      <CustomAlert
        isOpen={showDeleteAlert}
        title="Delete Customer"
        message={`Are you sure you want to delete customer "${customerToDelete?.name}"? This action cannot be undone.`}
        type="confirm"
        onConfirm={confirmDeleteCustomer}
        onCancel={cancelDeleteCustomer}
        confirmText="Delete"
        cancelText="Cancel"
      />

    </div>
  );
}
